import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react'
import { Context } from '@/components/Context'
import Loader from '@/components/Loader'
import handleViewport, { InjectedViewportProps } from 'react-in-viewport'
import { ArtData } from '@/database/art'
import { Image } from '@/database/image'

const getMaximumWidth = (maxWidth: boolean, fixedWidth: number, image: Image | undefined): number => {
    if (maxWidth) {
        if (image && window.innerWidth < image.width) {
            return window.innerWidth
        } else if (image) {
            return image.width
        }
    }
    return fixedWidth
}

const LoadImageButtonOnDemand = ({
    artData,
    style,
    alt,
    id,
    objectFit,
    layout,
    maxWidth = false,
    fixedWidth = 350,
    onClick,
}: {
    artData: ArtData
    style?: string
    alt?: string
    id?: string
    layout?: any
    objectFit?: any
    maxWidth?: boolean
    fixedWidth?: number
    onClick?: (artData: ArtData) => void
}): ReactElement => {
    const { fetchImage, getCachedImage } = useContext(Context)
    const [image, setImage] = React.useState(getCachedImage(artData.image.id))
    const [imageRequest, setImageRequest] = useState(false)
    const requestedOnce = useRef(false)
    const maximumWidth = getMaximumWidth(maxWidth, fixedWidth, image)

    const Block = React.memo((props: InjectedViewportProps<HTMLDivElement>) => {
        const { forwardedRef } = props
        return (
            <div ref={forwardedRef} className="viewport-block">
                {image?.data ? (
                    <button
                        id={id}
                        className="image-button"
                        onClick={() => (onClick ? onClick(artData) : undefined)}
                        style={{
                            width: `${maximumWidth}px`,
                            height: `${(image.height * maximumWidth) / image.width}px`,
                            backgroundImage: `url(${image.data})`,
                            backgroundSize: 'cover', // Ensures the image covers the button area
                            border: 'none', // Removes the default button border
                            padding: '0', // Removes the default button padding
                            position: 'relative', // Needed for absolute positioning of the dots
                        }}
                    >
                        {onClick && (
                            <span className="dots-overlay" aria-hidden="true">
                                <span className="dot"></span>
                                <span className="dot"></span>
                                <span className="dot"></span>
                            </span>
                        )}
                    </button>
                ) : (
                    <Loader
                        width={maximumWidth}
                        height={image ? (image.height * maximumWidth) / image.width : maximumWidth}
                    />
                )}
            </div>
        )
    })

    Block.displayName = 'Block'

    useEffect(() => {
        const getImage = async () => {
            return await fetchImage(artData.image.id)
        }

        if (!requestedOnce.current && imageRequest && !image) {
            getImage().then((image) => {
                requestedOnce.current = true
                if (image?.data) {
                    setImage(image)
                }
            })
        }
    }, [imageRequest])

    const ViewportBlock = handleViewport(Block)
    return (
        <ViewportBlock
            onEnterViewport={() => {
                if (!image?.data) {
                    setImageRequest(true)
                }
            }}
        />
    )
}

export default LoadImageButtonOnDemand
